<template>
  <div class="cms-team-member-form">
    <cms-form-header
      :title="isEditing ? $t('cms.team.editMember') : $t('cms.team.createMember')"
      :disabled="isSaving || isLoadingMember"
      @back="goBack"
      @save-draft="saveDraft"
      @save-publish="saveAndPublish"
    />

    <cms-form-layout>
      <form @submit.prevent="handleSubmit">
        <cms-form-section :title="$t('cms.team.generalInfo')">
          <div class="cms-form-grid">
            <cms-input
              v-model.number="form.order"
              type="number"
              :label="$t('cms.team.order')"
              :help-text="$t('cms.team.orderHelp')"
            />
            <cms-select
              v-model="guiderSelectValue"
              :label="$t('cms.team.linkedGuider')"
              :options="guiderOptions"
              :placeholder="$t('cms.team.linkedGuiderPlaceholder')"
              :full-width="true"
            />
            <cms-input
              v-model="form.linkedin"
              type="url"
              :label="$t('cms.team.linkedin')"
              :placeholder="$t('cms.team.linkedinPlaceholder')"
              :full-width="true"
            />
            <cms-input
              v-model="form.instagram"
              type="url"
              :label="$t('cms.team.instagram')"
              :placeholder="$t('cms.team.instagramPlaceholder')"
              :full-width="true"
            />
          </div>
        </cms-form-section>

        <cms-form-section :title="$t('cms.team.translationsSection')">
          <cms-translation-tabs :languages="languages" v-model="currentLanguage">
            <template #default="{ locale }">
              <div class="cms-form-grid">
                <cms-input
                  v-model="getTranslation(locale).name"
                  :label="`${$t('cms.team.name')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-input
                  v-model="getTranslation(locale).role"
                  :label="`${$t('cms.team.role')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-editor
                  v-model="getTranslation(locale).bio"
                  :label="`${$t('cms.team.bio')} (${locale.toUpperCase()})`"
                />
                <cms-facts-input
                  v-model="getTranslation(locale).facts"
                  :label="`${$t('cms.team.facts')} (${locale.toUpperCase()})`"
                  :question-label="$t('cms.team.factQuestion')"
                  :answer-label="$t('cms.team.factAnswer')"
                  :add-button-text="$t('cms.team.addFact')"
                  :full-width="true"
                />
              </div>
            </template>
          </cms-translation-tabs>
        </cms-form-section>
      </form>

      <template #sidebar>
        <cms-sidebar>
          <cms-sidebar-section :title="$t('cms.team.publishing')">
            <label class="cms-checkbox-item">
              <input v-model="form.is_public" type="checkbox" class="cms-checkbox" style="margin-right: 8px;" />
              <span>{{ $t('cms.team.isPublic') }}</span>
            </label>
          </cms-sidebar-section>

          <cms-sidebar-section :title="$t('cms.team.mediaSection')">
            <template v-if="memberId">
              <cms-image-upload
                ref="featuredImageUploadRef"
                v-model="form.featured_image"
                :label="$t('cms.team.featuredImage')"
                :help-text="$t('cms.team.featuredImageHelp')"
                :initial-media="featuredUploaderInitialMedia"
                @uploaded="handleFeaturedUploaded"
                @removed="handleFeaturedRemoved"
              />
              <div class="cms-media-actions" :class="{ 'is-media-saving': isSyncingFeaturedMedia }">
                <cms-button
                  variant="secondary"
                  size="sm"
                  :disabled="!canSaveFeaturedMedia || isSyncingFeaturedMedia"
                  @click="saveFeaturedMedia"
                >
                  <v-icon icon="upload" size="sm" />
                  {{ isSyncingFeaturedMedia ? $t('general.button.saving') : $t('cms.team.uploadFeatured') }}
                </cms-button>
              </div>
              <cms-input
                v-model="getTranslation(currentLanguage).featured_image_alt"
                :label="`${$t('cms.team.featuredImageAlt')} (${currentLanguage.toUpperCase()})`"
                :full-width="true"
                :disabled="isSaving || isLoadingMember"
              />
            </template>
            <template v-else>
              <p class="cms-form-help cms-media-disabled">
                {{ $t('cms.activities.mediaUploadRequiresCreation') }}
              </p>
            </template>
          </cms-sidebar-section>
        </cms-sidebar>
      </template>
    </cms-form-layout>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import type { TeamFact } from '@/components/cms/CmsFactsInput.vue';
import {
  CmsFormHeader,
  CmsFormLayout,
  CmsFormSection,
  CmsInput,
  CmsSelect,
  CmsButton,
  CmsEditor,
  CmsSidebar,
  CmsSidebarSection,
  CmsTranslationTabs,
  CmsImageUpload,
  CmsFactsInput,
} from '@/components/cms';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';
import { hasPendingCmsMediaSave, persistCmsMediaAttachment } from '@/helpers/cmsMediaUpload';
import { runCmsMediaSync } from '@/helpers/cmsMediaSaveFlow';

interface TeamMemberTranslation {
  locale: string;
  name: string;
  role: string;
  bio: string;
  facts: TeamFact[];
  featured_image_alt: string;
}

interface TeamMemberForm {
  cms_guider_id: number | null;
  order: number;
  is_public: boolean;
  linkedin: string;
  instagram: string;
  featured_image: string;
  translations: TeamMemberTranslation[];
}

interface SelectOption {
  value: number | string;
  label: string;
}

export default defineComponent({
  name: 'TheCmsTeamMemberForm',
  components: {
    CmsFormHeader,
    CmsFormLayout,
    CmsFormSection,
    CmsInput,
    CmsSelect,
    CmsButton,
    CmsEditor,
    CmsSidebar,
    CmsSidebarSection,
    CmsTranslationTabs,
    CmsImageUpload,
    CmsFactsInput,
    VIcon,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const memberId = ref(route.params.id ? Number(route.params.id) : null);
    const isEditing = computed(() => memberId.value !== null);

    const { languages, locales, resolveStoredLocale } = useAppLanguages();
    const currentLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));

    const createEmptyTranslation = (locale: string): TeamMemberTranslation => ({
      locale,
      name: '',
      role: '',
      bio: '',
      facts: [],
      featured_image_alt: '',
    });

    const form = reactive<TeamMemberForm>({
      cms_guider_id: null,
      order: 0,
      is_public: false,
      linkedin: '',
      instagram: '',
      featured_image: '',
      translations: locales.value.map((locale) => createEmptyTranslation(locale)),
    });

    const guiders = ref<SelectOption[]>([]);
    const guiderOptions = computed(() => [{ value: '', label: t('cms.team.noLinkedGuider') }, ...guiders.value]);
    const guiderSelectValue = computed<string | number>({
      get: () => (form.cms_guider_id === null ? '' : form.cms_guider_id),
      set: (value) => {
        if (value === '' || value === null) {
          form.cms_guider_id = null;
          return;
        }
        const parsed = Number(value);
        form.cms_guider_id = Number.isNaN(parsed) ? null : parsed;
      },
    });

    const isSaving = ref(false);
    const isLoadingMember = ref(false);

    const getTranslation = (locale: string): TeamMemberTranslation => {
      let translation = form.translations.find((entry) => entry.locale === locale);
      if (!translation) {
        translation = createEmptyTranslation(locale);
        form.translations.push(translation);
      }
      if (!translation.facts) translation.facts = [];
      return translation;
    };

    const stripHtmlContent = (value: string) =>
      value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

    const translationsAreComplete = computed(() =>
      form.translations.some(
        (translation) => translation.name.trim() && stripHtmlContent(translation.bio || '').length > 0,
      ),
    );

    const validateTranslations = () => {
      if (translationsAreComplete.value) return true;
      toast.error(t('cms.team.translationValidationError'));
      return false;
    };

    const buildPayload = () => {
      const translationsPayload = form.translations
        .map((translation) => ({
          locale: translation.locale,
          name: translation.name?.trim(),
          role: translation.role?.trim() || null,
          bio: translation.bio,
          facts: (translation.facts || []).filter((f) => f.question?.trim() && f.answer?.trim()),
          featured_image_alt: translation.featured_image_alt?.trim() || '',
        }))
        .filter((translation) => translation.name && stripHtmlContent(translation.bio || '').length > 0);

      return {
        cms_guider_id: form.cms_guider_id,
        order: form.order ?? 0,
        is_public: form.is_public,
        linkedin: form.linkedin?.trim() || null,
        instagram: form.instagram?.trim() || null,
        translations: translationsPayload,
      };
    };

    const extractMemberData = (response: any) => {
      if (!response) return null;
      return normalizeResource(response?.data) || normalizeResource(response) || response?.data || response;
    };

    const extractMemberId = (response: any): number | null => {
      const data = extractMemberData(response);
      return typeof data?.id === 'number' ? data.id : null;
    };

    const extractListData = (response: any): any[] => {
      const candidates = [response?.data?.data, response?.data, response];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
      }
      return [];
    };

    const loadGuiders = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.guiderService.listCmsGuiders(agencySlug.value, { perPage: 100 });
        const list = extractListData(response);
        guiders.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          const translations = extractRelationship(normalized, 'translations');
          const normalizedTranslations = normalizeResource(translations);
          const translation = Array.isArray(normalizedTranslations)
            ? normalizedTranslations[0]
            : normalizedTranslations;
          return {
            value: normalized.id,
            label: translation?.title || translation?.name || `#${normalized.id}`,
          };
        });
      } catch (error) {
        console.error('Failed to load guiders', error);
      }
    };

    const applyTranslationsFromMember = (member: any, locale: string) => {
      const translations = extractRelationship(member, 'translations');
      const normalized = normalizeResource(translations);
      const list = Array.isArray(normalized) ? normalized : normalized ? [normalized] : [];
      const translation = list.find((tr: any) => (tr?.locale || '').toLowerCase() === locale) || list[0];
      if (!translation) return;
      const target = getTranslation(locale);
      target.name = translation.name || '';
      target.role = translation.role || '';
      target.bio = translation.bio || '';
      target.facts = Array.isArray(translation.facts) ? translation.facts : [];
      target.featured_image_alt = translation.featured_image_alt || '';
    };

    const mapMemberToForm = (member: any) => {
      const normalized = normalizeResource(member) || member;
      form.cms_guider_id = normalized.cmsGuiderId ?? normalized.cms_guider_id ?? null;
      form.order = normalized.order ?? 0;
      form.is_public = !!normalized.isPublic;
      form.linkedin = normalized.linkedin || '';
      form.instagram = normalized.instagram || '';
      mapMemberMediaCollections(normalized);
    };

    const loadMember = async () => {
      if (!agencySlug.value || !memberId.value) return;
      isLoadingMember.value = true;
      try {
        const response = await api.teamMemberService.getCmsTeamMember(agencySlug.value, memberId.value);
        const memberData = extractMemberData(response);
        if (memberData) {
          mapMemberToForm(memberData);
          for (const locale of locales.value) {
            applyTranslationsFromMember(memberData, locale);
          }
        }
      } catch (error) {
        console.error('Failed to load team member', error);
        toast.error(t('general.errorMessage'));
      } finally {
        isLoadingMember.value = false;
      }
    };

    const CMS_MEDIA_MODEL_TYPE = 'cms_team_member';
    const FEATURED_MEDIA_COLLECTION = 'cms_team_member_featured';
    const MEDIA_DRIVER = 's3';

    const featuredImageUploadRef = ref<any>(null);
    const featuredUploaderInitialMedia = ref<any[]>([]);
    const featuredMediaPayload = ref<any[] | null>(null);
    const pendingFeaturedDeletion = ref<any | null>(null);
    const isSyncingFeaturedMedia = ref(false);

    const cloneMediaItems = (items: any[] = []) => items.map((item) => JSON.parse(JSON.stringify(item)));

    const getCollectionName = (item: any) => item?.collection_name || item?.collectionName || '';
    const getCustomProperties = (item: any) => item?.custom_properties || item?.customProperties || {};
    const getOrderColumn = (item: any) => item?.order_column ?? item?.orderColumn;

    const getMediaUrl = (item: any) => {
      if (!item) return '';
      const attributes = item.attributes || {};
      return (
        item.storage_url ||
        item.storageUrl ||
        attributes.storage_url ||
        attributes.storageUrl ||
        item.original_url ||
        item.originalUrl ||
        attributes.original_url ||
        attributes.originalUrl ||
        item.preview_url ||
        attributes.preview_url ||
        ''
      );
    };

    const getMediaPreviewUrl = (item: any, fallback: string) => {
      const attributes = item?.attributes || {};
      return (
        item.preview_url ||
        item.previewUrl ||
        attributes.preview_url ||
        attributes.previewUrl ||
        fallback
      );
    };

    const normalizeMediaForUploader = (item: any, fallbackOrder = 1) => {
      if (!item) return null;
      const customProps = getCustomProperties(item);
      const order = customProps?.order ?? getOrderColumn(item) ?? fallbackOrder;
      const storageUrl = getMediaUrl(item);
      const previewUrl = getMediaPreviewUrl(item, storageUrl);

      return {
        ...item,
        order,
        order_column: order,
        original_url: storageUrl,
        storage_url: storageUrl,
        preview_url: previewUrl,
        attributes: {
          ...(item.attributes || {}),
          uuid: item.uuid,
          name: item.name || `media-${order}`,
          order_column: order,
          collection_name: getCollectionName(item),
          original_url: storageUrl,
          preview_url: previewUrl,
          isExisting: true,
          urls: {
            ...(item.attributes?.urls || {}),
            original: storageUrl,
            preview: previewUrl,
          },
        },
      };
    };

    const extractMediaItems = (member: any) => {
      const mediaRelationship = extractRelationship(member, 'media');
      const normalizedRelationship = normalizeResource(mediaRelationship);
      if (Array.isArray(normalizedRelationship)) {
        return normalizedRelationship;
      }
      if (mediaRelationship?.data && Array.isArray(mediaRelationship.data)) {
        return mediaRelationship.data;
      }
      if (Array.isArray(member.media)) {
        return member.media;
      }
      return [];
    };

    const mapMemberMediaCollections = (member: any) => {
      const mediaItems = extractMediaItems(member)
        .map((item: any) => normalizeResource(item) || item)
        .filter((item: any) => !!item && (item.uuid || item.id));

      const featuredItems = mediaItems
        .filter((item: any) => getCollectionName(item) === FEATURED_MEDIA_COLLECTION)
        .map((item: any, index: number) => normalizeMediaForUploader(item, index + 1))
        .filter(Boolean);

      featuredUploaderInitialMedia.value = cloneMediaItems(featuredItems);
      pendingFeaturedDeletion.value = null;

      const featuredUrl = getMediaUrl(featuredItems[0]);
      form.featured_image = featuredUrl || '';
    };

    const deleteMediaItemsByIds = async (ids: Array<number | null | undefined>) => {
      const uniqueIds = Array.from(
        new Set((ids || []).filter((id): id is number => typeof id === 'number' && id > 0)),
      );
      if (!uniqueIds.length) {
        return;
      }
      await Promise.all(uniqueIds.map((id) => api.media.delete(MEDIA_DRIVER, id)));
    };

    const buildMediaConfirmEntries = (mediaItems: any[] = [], collection: string) => {
      return (Array.isArray(mediaItems) ? mediaItems : [])
        .map((item: any, index: number) => {
          const attributes = item?.attributes ?? {};
          if (attributes.isExisting) {
            return null;
          }
          const uuid = attributes.uuid ?? item.uuid;
          if (!uuid) {
            return null;
          }
          const order = attributes.order ?? attributes.order_column ?? item.order ?? index + 1;
          return {
            uuid,
            name: attributes.name ?? item.name ?? `media-${order}`,
            order,
            collection,
          };
        })
        .filter((entry): entry is { uuid: string; name: string; order: number; collection: string } => !!entry);
    };

    const refreshMemberMediaState = async () => {
      if (!agencySlug.value || !memberId.value) {
        return;
      }
      try {
        const response = await api.teamMemberService.getCmsTeamMember(agencySlug.value, memberId.value);
        const memberData = extractMemberData(response);
        if (memberData) {
          mapMemberMediaCollections(memberData);
        }
      } catch (error) {
        console.error('Error refreshing team member media', error);
      }
    };

    const confirmFeaturedMedia = async () => {
      if (!memberId.value || isSyncingFeaturedMedia.value || isLoadingMember.value) {
        return;
      }
      const payload = featuredMediaPayload.value ?? [];
      const hasUploads = hasPendingCmsMediaSave(payload, FEATURED_MEDIA_COLLECTION);
      const hasPendingDeletion = !!pendingFeaturedDeletion.value?.id;
      if (!hasUploads && !hasPendingDeletion) {
        return;
      }

      try {
        await runCmsMediaSync(isSyncingFeaturedMedia, async () => {
          if (hasPendingDeletion) {
            await deleteMediaItemsByIds([pendingFeaturedDeletion.value.id]);
            pendingFeaturedDeletion.value = null;
          } else if (hasUploads && featuredUploaderInitialMedia.value.length > 0) {
            await deleteMediaItemsByIds(featuredUploaderInitialMedia.value.map((item: any) => item.id));
          }
          if (hasUploads) {
            await persistCmsMediaAttachment({
              api,
              driver: MEDIA_DRIVER,
              agencySlug: agencySlug.value!,
              mediaItems: payload,
              modelType: CMS_MEDIA_MODEL_TYPE,
              modelId: memberId.value,
              collection: FEATURED_MEDIA_COLLECTION,
            });
          }
          featuredMediaPayload.value = null;
          featuredImageUploadRef.value?.clearMedia?.();
          await refreshMemberMediaState();
        });
        toast.success(t('cms.activities.featuredMediaSaveSuccess'));
      } catch (error) {
        console.error('Error saving featured media', error);
        toast.error(t('general.errorMessage'));
      }
    };

    const persistPendingMediaChanges = async () => {
      await confirmFeaturedMedia();
    };

    const handleFeaturedUploaded = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
    };

    const handleFeaturedRemoved = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
      if ((!media || media.length === 0) && featuredUploaderInitialMedia.value.length > 0) {
        pendingFeaturedDeletion.value = featuredUploaderInitialMedia.value[0];
      }
    };

    const canSaveFeaturedMedia = computed(
      () =>
        !!memberId.value &&
        featuredMediaPayload.value !== null &&
        !isSyncingFeaturedMedia.value &&
        !isSaving.value &&
        !isLoadingMember.value,
    );

    const saveFeaturedMedia = async () => {
      await confirmFeaturedMedia();
    };

    const persistMember = async (publish = false) => {
      if (!agencySlug.value || !validateTranslations()) return;
      const payload = buildPayload();
      isSaving.value = true;
      try {
        if (isEditing.value && memberId.value) {
          await api.teamMemberService.updateCmsTeamMember(agencySlug.value, memberId.value, payload);
        } else {
          const response = await api.teamMemberService.createCmsTeamMember(agencySlug.value, payload);
          const newId = extractMemberId(response);
          if (newId) {
            memberId.value = newId;
            await router.replace({ name: 'cms.team-members.edit', params: { id: newId } });
          }
        }

        if (publish && memberId.value) {
          await api.teamMemberService.publishCmsTeamMember(agencySlug.value, memberId.value, { is_public: true });
          form.is_public = true;
        }

        await persistPendingMediaChanges();
        toast.success(publish ? t('cms.team.publishSuccess') : t('general.shared.savedChanges'));
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        isSaving.value = false;
      }
    };

    const saveDraft = () => persistMember(false);
    const saveAndPublish = () => persistMember(true);
    const handleSubmit = () => saveDraft();
    const goBack = () => router.push({ name: 'cms.team-members' });

    watch(
      () => route.params.id,
      (newId) => {
        memberId.value = newId ? Number(newId) : null;
        if (memberId.value) {
          loadMember();
        } else {
          form.cms_guider_id = null;
          form.order = 0;
          form.is_public = false;
          form.featured_image = '';
          form.translations = locales.value.map((locale) => createEmptyTranslation(locale));
          featuredUploaderInitialMedia.value = [];
          featuredMediaPayload.value = null;
          pendingFeaturedDeletion.value = null;
        }
      },
      { immediate: true },
    );

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          loadGuiders();
          if (memberId.value) loadMember();
        }
      },
      { immediate: true },
    );

    return {
      form,
      isEditing,
      isSaving,
      isLoadingMember,
      memberId,
      languages,
      currentLanguage,
      guiderOptions,
      guiderSelectValue,
      getTranslation,
      featuredImageUploadRef,
      featuredUploaderInitialMedia,
      canSaveFeaturedMedia,
      handleFeaturedUploaded,
      handleFeaturedRemoved,
      saveFeaturedMedia,
      saveDraft,
      saveAndPublish,
      handleSubmit,
      goBack,
    };
  },
});
</script>
